import { useEffect, useState, useMemo } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Settings, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

/**
 * ConfigInspector — admin-only card on /admin. Fetches
 * GET /api/admin/config-status and renders one read-only row per integration
 * so the operator can eyeball at launch what's live, what's still in
 * preview/test mode, and what's missing. Never shows the raw secret — the
 * backend only returns a masked head…tail string.
 */
const STATUS = {
  live:    { Icon: CheckCircle2, tone: "text-[#00FF9D]",  ring: "border-[#00FF9D]/30 bg-[#00FF9D]/[0.05]", label: "Live" },
  preview: { Icon: AlertTriangle, tone: "text-[#ffb067]", ring: "border-[#FF7B00]/30 bg-[#FF7B00]/[0.05]", label: "Preview" },
  missing: { Icon: XCircle, tone: "text-rose-300",        ring: "border-rose-400/30 bg-rose-500/[0.05]",  label: "Missing" },
};

export default function ConfigInspector() {
  const [state, setState] = useState({ loading: true, err: null, data: null });

  const load = async () => {
    setState((s) => ({ ...s, loading: true, err: null }));
    try {
      const { data } = await api.get("/admin/config-status");
      setState({ loading: false, err: null, data });
    } catch (e) {
      setState({ loading: false, err: e?.response?.data?.detail || e.message, data: null });
    }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = new Map();
    (state.data?.integrations || []).forEach((i) => {
      if (!g.has(i.group)) g.set(i.group, []);
      g.get(i.group).push(i);
    });
    return Array.from(g.entries());
  }, [state.data]);

  const totals = state.data?.totals || { live: 0, preview: 0, missing: 0 };
  const ready = Boolean(state.data?.production_ready);

  return (
    <section className="glass p-6 space-y-5" data-testid="admin-config-inspector">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow inline-flex items-center gap-1.5">
            <Settings size={12} className="text-[#8aa8ff]" /> PRODUCTION CONFIG
          </div>
          <h2 className="font-display text-2xl tracking-tight mt-1">Integration Inspector</h2>
          <p className="text-white/55 text-sm mt-1">
            Read-only snapshot of every third-party key. Secrets are masked — only presence + shape are exposed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill kind="live" count={totals.live} />
          <StatusPill kind="preview" count={totals.preview} />
          <StatusPill kind="missing" count={totals.missing} />
          <button
            onClick={load}
            disabled={state.loading}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 bg-white/[0.04] text-white/75 hover:bg-white/10 transition disabled:opacity-60"
            data-testid="btn-config-refresh"
          >
            <RefreshCw size={12} className={state.loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {state.data && (
        <div
          className={`rounded-xl border px-4 py-2.5 text-sm flex items-center gap-2 ${
            ready
              ? "border-[#00FF9D]/40 bg-[#00FF9D]/[0.05] text-[#b4ffdb]"
              : "border-[#FF7B00]/40 bg-[#FF7B00]/[0.05] text-[#ffcfa0]"
          }`}
          data-testid="config-ready-banner"
        >
          {ready ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
          {ready
            ? "All integrations wired. Production-ready."
            : `${totals.missing + totals.preview} integration${totals.missing + totals.preview === 1 ? "" : "s"} still on preview/missing — add the remaining secrets in Deploy → Secrets before going live.`}
        </div>
      )}

      {state.err && (
        <div className="rounded-xl border border-rose-400/40 bg-rose-500/[0.05] text-rose-100 text-sm p-3" data-testid="config-error">
          Failed to load config: {state.err}
        </div>
      )}

      {state.loading && <div className="h-4 w-48 rounded bg-white/5 sheen" data-testid="config-loading" />}

      {grouped.map(([group, rows]) => (
        <div key={group} className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-white/45 font-display">{group}</div>
          <div className="rounded-xl border border-white/10 overflow-hidden divide-y divide-white/5 bg-white/[0.015]">
            {rows.map((r) => <Row key={r.key} r={r} />)}
          </div>
        </div>
      ))}
    </section>
  );
}

function Row({ r }) {
  const s = STATUS[r.status] || STATUS.missing;
  const { Icon } = s;
  return (
    <div
      className="flex items-start gap-3 px-4 py-3 hover:bg-white/[0.02] transition"
      data-testid={`config-row-${r.key}`}
    >
      <Icon size={15} className={`${s.tone} mt-0.5 shrink-0`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display text-[14px] tracking-tight text-white">{r.name}</div>
          <span className="text-[10px] font-mono-oak text-white/40">{r.key}</span>
        </div>
        <div className="text-[12px] text-white/55 mt-0.5 flex flex-wrap items-center gap-2">
          <span>{r.hint}</span>
          {r.masked && (
            <code
              className="font-mono-oak text-[11px] text-white/75 bg-white/[0.04] px-1.5 py-0.5 rounded"
              data-testid={`config-mask-${r.key}`}
            >
              {r.masked}
            </code>
          )}
        </div>
      </div>
      <span
        className={`shrink-0 text-[10px] font-display font-semibold px-2 py-0.5 rounded-full border ${s.ring} ${s.tone}`}
      >
        {s.label}
      </span>
    </div>
  );
}

function StatusPill({ kind, count }) {
  const s = STATUS[kind];
  const { Icon } = s;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-display font-semibold px-2 py-1 rounded-full border ${s.ring} ${s.tone}`}
      data-testid={`config-total-${kind}`}
    >
      <Icon size={11} /> {count} {s.label}
    </span>
  );
}
